import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import branchActions from "../../../../store/branchStore/branchActions";
import branchApi from "../../../../store/branchStore/branchApi";
import PageHeader from "../../../../components/PageHeader";
import Checkbox from "../../../../components/FormElements/Checkbox";
import CustomPhotoDropzone from "../../../../components/CustomImageDropzone";
import styles from './qrRegistration.module.scss';

const initialState = {
    enabled: false,
    logo_url: '',
    background_color: '#FFFFFF',
    accent_color: '#73D389',
    text_color: '#000000',
    wallet_enabled: false,
    show_branch_name: true,
    show_subtitle: true,
    show_first_name: true,
    show_last_name: true,
    show_birthday: false,
    show_gender: false,
    registration_bonus_enabled: false,
    registration_bonus_amount: 0,
};

export default function QrRegistration() {
    const dispatch = useDispatch();
    const info = useSelector((s) => s.branchReducer.info);
    const { t } = useTranslation();
    const [state, setState] = useState(initialState);
    const [pending, setPending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [qrImageUrl, setQrImageUrl] = useState(null);

    useEffect(() => {
        if (info) {
            setLoading(true);
            branchApi.getQrRegistrationConfig(info.id)
                .then((res) => {
                    if (res && res.data) {
                        setState({
                            enabled: res.data.enabled || false,
                            logo_url: res.data.logo_url || '',
                            background_color: res.data.background_color || '#FFFFFF',
                            accent_color: res.data.accent_color || '#73D389',
                            text_color: res.data.text_color || '#000000',
                            wallet_enabled: res.data.wallet_enabled || false,
                            show_branch_name: res.data.show_branch_name !== undefined ? res.data.show_branch_name : true,
                            show_subtitle: res.data.show_subtitle !== undefined ? res.data.show_subtitle : true,
                            show_first_name: res.data.show_first_name !== undefined ? res.data.show_first_name : true,
                            show_last_name: res.data.show_last_name !== undefined ? res.data.show_last_name : true,
                            show_birthday: res.data.show_birthday || false,
                            show_gender: res.data.show_gender || false,
                            registration_bonus_enabled: res.data.registration_bonus_enabled || false,
                            registration_bonus_amount: res.data.registration_bonus_amount || 0,
                        });
                    }
                })
                .catch(() => {})
                .finally(() => setLoading(false));

            branchApi.getQrRegistrationQRImage(info.id)
                .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    setQrImageUrl(url);
                })
                .catch(() => {});
        }
    }, [info]);

    const onSubmit = (e) => {
        e.preventDefault();
        if (!info) return;

        setPending(true);
        branchApi.updateQrRegistrationConfig(info.id, state)
            .then(() => {
                dispatch(branchActions.getBranch(info.id));
            })
            .finally(() => {
                setPending(false);
            });
    };

    const downloadQR = () => {
        if (!qrImageUrl || !info) return;
        const a = document.createElement('a');
        a.href = qrImageUrl;
        a.download = `qr-registration-${info.id}.png`;
        a.click();
    };

    const printQR = () => {
        if (!qrImageUrl) return;
        const win = window.open('');
        win.document.write(`
            <html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0">
                <img src="${qrImageUrl}" style="width:400px;height:400px"/>
            </body></html>
        `);
        win.document.close();
        win.focus();
        win.print();
        win.close();
    };

    const handleLogoUpload = (files) => {
        if (!files || files.length === 0 || !info) return;

        const formData = new FormData();
        formData.append('logo', files[0]);

        setPending(true);
        branchApi.uploadQrLogo(info.id, formData)
            .then((res) => {
                if (res && res.data && res.data.url) {
                    setState(prev => ({ ...prev, logo_url: res.data.url }));
                }
            })
            .finally(() => setPending(false));
    };

    const qrToggle = info?.configuration?.access_toggles?.qr_registration;
    if (info && qrToggle !== undefined && !qrToggle?.enabled) {
        return (
            <div className={styles.container} style={{ textAlign: 'center', padding: '60px 20px' }}>
                <p style={{ fontSize: 16, color: '#888' }}>
                    {t('optionForbidden')} <br/>
                    {t('touchModerator')}
                </p>
            </div>
        );
    }

    if (loading) {
        return <div className={styles.container}><p>Загрузка...</p></div>;
    }

    const qrLink = info ? `dos.sagibonus.com/qr/${info.id}` : '';

    return (
        <div className={styles.container}>
            <PageHeader title="QR Регистрация">
                <p>Настройте страницу регистрации по QR-коду для ваших клиентов</p>
            </PageHeader>

            <form onSubmit={onSubmit}>
                {/* Включить/выключить */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Статус</div>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <Checkbox
                            label="Включено"
                            checked={state.enabled}
                            onChange={() => setState({ ...state, enabled: true })}
                        />
                        <Checkbox
                            checked={!state.enabled}
                            onChange={() => setState({ ...state, enabled: false })}
                            label="Выключено"
                        />
                    </div>
                </div>

                {/* QR код */}
                {info && (
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>QR код</div>
                        <p style={{ fontSize: 14, color: '#666', marginBottom: 12 }}>
                            Распечатайте и разместите в вашем заведении — клиенты отсканируют и зарегистрируются.
                        </p>
                        <div className={styles.qrLink} style={{ marginBottom: 16 }}>
                            <span className={styles.qrLinkText}>{qrLink}</span>
                        </div>
                        {qrImageUrl && (
                            <div className={styles.qrImageWrapper}>
                                <img
                                    src={qrImageUrl}
                                    alt="QR код регистрации"
                                    className={styles.qrImage}
                                />
                                <div className={styles.qrActions}>
                                    <ButtonFilled type="button" onClick={downloadQR}>
                                        Скачать PNG
                                    </ButtonFilled>
                                    <ButtonFilled type="button" onClick={printQR}>
                                        Распечатать
                                    </ButtonFilled>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Логотип */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Логотип</div>
                    {state.logo_url && (
                        <img src={state.logo_url} alt="Логотип" className={styles.logoPreview} />
                    )}
                    <CustomPhotoDropzone
                        onChange={handleLogoUpload}
                        pending={pending}
                    />
                </div>

                {/* Цвета */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Цвета</div>

                    <div className={styles.colorRow}>
                        <span className={styles.label}>Цвет фона</span>
                        <input
                            type="color"
                            className={styles.colorInput}
                            value={state.background_color}
                            onChange={(e) => setState({ ...state, background_color: e.target.value })}
                        />
                        <span className={styles.colorHex}>{state.background_color}</span>
                    </div>

                    <div className={styles.colorRow}>
                        <span className={styles.label}>Акцентный цвет (кнопки)</span>
                        <input
                            type="color"
                            className={styles.colorInput}
                            value={state.accent_color}
                            onChange={(e) => setState({ ...state, accent_color: e.target.value })}
                        />
                        <span className={styles.colorHex}>{state.accent_color}</span>
                    </div>

                    <div className={styles.colorRow}>
                        <span className={styles.label}>Цвет текста</span>
                        <input
                            type="color"
                            className={styles.colorInput}
                            value={state.text_color}
                            onChange={(e) => setState({ ...state, text_color: e.target.value })}
                        />
                        <span className={styles.colorHex}>{state.text_color}</span>
                    </div>
                </div>

                {/* Кошельки */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Кошельки (Apple / Google Wallet)</div>
                    <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
                        Если включено, после регистрации клиенту будет предложено добавить бонусную карту в кошелёк телефона.
                        Убедитесь что вы настроили параметры кошелька (цвет, изображение).
                    </p>
                    <div style={{ display: 'flex', gap: 16 }}>
                        <Checkbox
                            label="Включено"
                            checked={state.wallet_enabled}
                            onChange={() => setState({ ...state, wallet_enabled: true })}
                        />
                        <Checkbox
                            checked={!state.wallet_enabled}
                            onChange={() => setState({ ...state, wallet_enabled: false })}
                            label="Выключено"
                        />
                    </div>
                </div>

                {/* Отображение */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Отображение на странице</div>
                    <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
                        Выберите какие элементы показывать на странице регистрации.
                    </p>

                    <div className={styles.row}>
                        <span className={styles.label}>Название бизнеса</span>
                        <Checkbox
                            label=""
                            checked={state.show_branch_name}
                            onChange={() => setState({ ...state, show_branch_name: !state.show_branch_name })}
                        />
                    </div>

                    <div className={styles.row}>
                        <span className={styles.label}>Текст «Заполните форму для регистрации»</span>
                        <Checkbox
                            label=""
                            checked={state.show_subtitle}
                            onChange={() => setState({ ...state, show_subtitle: !state.show_subtitle })}
                        />
                    </div>
                </div>

                {/* Поля формы */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Поля формы регистрации</div>
                    <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
                        Номер телефона всегда обязателен. Выберите какие дополнительные поля показывать клиентам.
                    </p>

                    <div className={styles.row}>
                        <span className={styles.label}>Имя</span>
                        <Checkbox
                            label=""
                            checked={state.show_first_name}
                            onChange={() => setState({ ...state, show_first_name: !state.show_first_name })}
                        />
                    </div>

                    <div className={styles.row}>
                        <span className={styles.label}>Фамилия</span>
                        <Checkbox
                            label=""
                            checked={state.show_last_name}
                            onChange={() => setState({ ...state, show_last_name: !state.show_last_name })}
                        />
                    </div>

                    <div className={styles.row}>
                        <span className={styles.label}>Дата рождения</span>
                        <Checkbox
                            label=""
                            checked={state.show_birthday}
                            onChange={() => setState({ ...state, show_birthday: !state.show_birthday })}
                        />
                    </div>

                    <div className={styles.row}>
                        <span className={styles.label}>Пол</span>
                        <Checkbox
                            label=""
                            checked={state.show_gender}
                            onChange={() => setState({ ...state, show_gender: !state.show_gender })}
                        />
                    </div>
                </div>

                {/* Бонус за регистрацию */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>Бонус за регистрацию через QR</div>
                    <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
                        Начислите бонус новым клиентам при регистрации через QR-код.
                    </p>
                    <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                        <Checkbox
                            label="Включено"
                            checked={state.registration_bonus_enabled}
                            onChange={() => setState({ ...state, registration_bonus_enabled: true })}
                        />
                        <Checkbox
                            checked={!state.registration_bonus_enabled}
                            onChange={() => setState({ ...state, registration_bonus_enabled: false })}
                            label="Выключено"
                        />
                    </div>
                    {state.registration_bonus_enabled && (
                        <div className={styles.row}>
                            <span className={styles.label}>Сумма бонуса</span>
                            <input
                                type="number"
                                min="0"
                                value={state.registration_bonus_amount}
                                onChange={(e) => setState({ ...state, registration_bonus_amount: Number(e.target.value) })}
                                className={styles.input}
                            />
                        </div>
                    )}
                </div>

                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: 16 }}>
                    <ButtonFilled
                        type="submit"
                        loading={pending}
                    >
                        Сохранить
                    </ButtonFilled>
                </div>
            </form>
        </div>
    );
}
